/**
 * The location control. Provides a button with location capabilities.
 *
 * The tomtom.location() method creates a new instance of control.
 *
 * @class location
 */
define('widgets/controls/location',['display/map'], function (L) {
    "use strict";

    var EVENTS = {
        LocationFound: "locationFound",
        LocationError: "locationError"
    };

    function getClassName(options) {
        return options.className || options.imageSrc ? options.className : "icon-locate_me_btn";
    }

    function setSizeStyle(style, size) {
        if (size) {
            style.width = size[0] + "px";
            style.height = size[1] + "px";
            style.backgroundSize = size[0] + "px " + size[1] + "px";
        }
    }

    var Location = L.Control.extend({

        includes: L.Mixin.Events,

        options: {
            imageSrc: null,
            size: L.Browser.retina ? [57, 57] : [34, 34],
            className: ""
        },

        createContainer: function () {
            var className = getClassName(this.options),
                container = L.DomUtil.create("div", "leaflet-control-location " + className);
            setSizeStyle(container.style, this.options.size);
            container.style.cursor = "pointer";
            if (!L.Browser.svg || this.options.imageSrc) {
                var img = L.DomUtil.create("img", "", container);
                img.src = this.options.imageSrc || L.Icon.Default.imagePath + "/locate_me_btn.png";
                img.style.cursor = "pointer";
            }
            return container;
        },

        /**
         * The method to instantiates the location control.
         * Executed when adding to map.
         *
         * @method onAdd
         * @param {Object} [map] Leaflet map to add control to.
         *
         * @example
         * ```javascript
         *
         * var locationInstance = tomtom.location().addTo(map);
         *
         * ```
         */
        onAdd: function (map) {
            var container = this.createContainer();

            L.DomEvent.addListener(container, "mousedown", map.locate, map);
            var control = this;
            map.on("locationfound", function (location) {
                control.fire(EVENTS.LocationFound, location);
            });
            map.on("locationerror", function (error) {
                control.fire(EVENTS.LocationError, error);
            });
            return container;
        },
        /**
         * Event that is fired when obtaining user's current location succeed.
         *
         * @event LocationFound
         * @param {Object} eventObject The found <a href="http://leafletjs.com/reference.html#location-event"
         *     target="_blank">location details</a>.
         * @param {String} type Event's name, equal to "locationFound".
         * @param {Element} target Event's target.
         *
         * @example
         * ```javascript
         *
         * var locationInstance = tomtom.location().addTo(map);
         * locationInstance.on(locationInstance.Event.locationFound, function (eventObject) {
         *      console.log(eventObject);
         * });
         * ```
         */
        /**
         * Event that is fired when obtaining user's current location fails.
         *
         * @event LocationError
         * @param {Object} eventObject All failure information.
         * @param {String} type Event's name, equal to "locationError".
         * @param {Element} target Event's target.
         *
         * @example
         * ```javascript
         *
         * var locationInstance = tomtom.location().addTo(map);
         * locationInstance.on(locationInstance.Event.LocationError, function (eventObject) {
         *      console.log(eventObject);
         * });
         * ```
         */
        Event: EVENTS
    });

    /**
     * The method to instantiates the location control. The options can be passed on creation.
     *
     * @method location
     * @param {Object} [options] Options to be passed to the routing call.
     * @param {String} [options.imageSrc] The image path for locate button. If this option is passed the button would
     *     be created as an <img> tag.
     * @param {Array} [options.size=[34,34]] Array of two integers (width, height). Allows to change size of the image,
     *     a useful option for SVG images. By default 34 by 34, for retina display size is doubled.
     * @param {String} [options.className] The class name of container element. With this option external icon library
     *     (i.e. Font Awsome) can be used for button display.
     *
     * @example
     * ```javascript
     *
     * var locationInstance = tomtom.location().addTo(map);
     *
     * locationInstance.on(locationInstance.Events.LocationFound, function (object) {
     *       console.log('LocationFound');
     *       console.log(object);
     * });
     *
     * locationInstance.on(locationInstance.Events.LocationError, function (object) {
     *       console.log('LocationError');
     *       console.log(object);
     *  });
     *
     * ```
     */
    return function (options) {
        return new Location(options);
    };
});
